import React from 'react';
import { UseFormRegister, FieldArrayWithId } from 'react-hook-form';
import { PaymentOrder, PaymentOrderItem } from '../../../types';
import Button from '../Button';
import Input from '../Input';

interface PaymentOrderItemsTableProps {
  register: UseFormRegister<PaymentOrder>;
  fields: FieldArrayWithId<PaymentOrder, 'items', 'id'>[];
  watchedItems: PaymentOrderItem[] | undefined;
  addItem: () => void;
  removeItem: (index: number) => void;
}

const PaymentOrderItemsTable: React.FC<PaymentOrderItemsTableProps> = ({
  register,
  fields,
  watchedItems,
  addItem,
  removeItem,
}) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Items</h2>
          <Button type="button" onClick={addItem} variant="secondary" size="sm">
            Add Item
          </Button>
        </div>
      </div>
      <div className="card-body">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">QTY</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {fields.map((field, index) => (
                <tr key={field.id}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {index + 1}
                  </td>
                  <td className="px-4 py-4">
                    <Input
                      {...register(`items.${index}.details`, { required: 'Details are required' })}
                      placeholder="Item details"
                      className="min-w-[200px]"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <Input
                      {...register(`items.${index}.unit`)}
                      placeholder="Unit"
                      className="min-w-[80px]"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register(`items.${index}.qty`, { 
                        required: 'Quantity is required',
                        min: { value: 0.01, message: 'Quantity must be greater than 0' }
                      })}
                      className="min-w-[80px]"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register(`items.${index}.unitPrice`, { 
                        required: 'Unit price is required',
                        min: { value: 0, message: 'Unit price must be 0 or greater' }
                      })}
                      className="min-w-[100px]"
                    />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {((watchedItems?.[index]?.qty || 0) * (watchedItems?.[index]?.unitPrice || 0)).toFixed(2)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {fields.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeItem(index)}
                        variant="danger"
                        size="sm"
                      >
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentOrderItemsTable;
